import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { getId } from '@cawita/core-front';
import { CwtApiService } from '@cawita/core-front/api';
import { CwtAuthStoreService, LoginResult } from '@cawita/core-front/auth';
import { CWT_APP_CONFIG, CwtAppConfig } from '@mobile/modules/core/core-app.config';
import { NotificationStore } from '@mobile/modules/notification';
import { LoggedProfileStore } from '@mobile/modules/profile';
import { SOCKET_LISTENER, SocketListener } from '@mobile/modules/socket/socket-listener';
import { StayStore } from '@mobile/modules/stay';
import { Partner, User } from '@shared/models';
import { Socket } from 'ngx-socket-io';
import { KatchaAuthStore } from '../stores';
import { DeepLinkService } from './deep-link.service';

@Injectable({providedIn: 'root'})
export class AuthFlowService {
  private _nextUrl: string = null;
  private _socketIsConnected = false;

  public get isLoggedIn() { return this.auth.isLoggedIn; }

  constructor(
    @Inject(CwtAuthStoreService) public auth: KatchaAuthStore,
    private stayStore: StayStore,
    private notificationStore: NotificationStore,
    private loggedProfileStore: LoggedProfileStore,
    private socket: Socket,
    private deepLink: DeepLinkService,
    private router: Router,
    private api: CwtApiService,
    @Inject(CWT_APP_CONFIG) private cfg: CwtAppConfig,
    @Inject(SOCKET_LISTENER) @Optional() private listeners: SocketListener[],
  ) {
    this.socket.on('connect', this._socketConnected);
    this.socket.on('connect_error', (err) => console.log(err));
    this.socket.on('disconnect', this._socketDisconnected);
  }

  public queueRedirect(path: string) {
    this._nextUrl = path;
  }

  public goLogin() {
    this.router.navigate(['/auth', 'login'], { replaceUrl: true });
  }

  public async initialize() {
    await this.deepLink.init();

    console.log(`this.auth`, this.auth);
    console.log(`this.auth`, this.auth.getValue());
    if(!this.auth.isLoggedIn) await this._onOffline();
    else await this._onOnline(this.auth.user);

    console.log('INIT SOCKET');
    this.socket.connect();
  }

  public async selectPartner(partner: Partner) {
    await this.stayStore.usePartner(partner);
    this.redirectToCachedUrlOrPath(['/home'], { replaceUrl: true});
  }

  public async logout() {
    console.log('LOGGGGOOOOUT');
    await this.stayStore.clearPartner();
    this.auth.logout(false);
    const activePartner = await this._onLogout();
    this._nextUrl = null;
    this._redirectOnLogout(activePartner);
  }

  public async loginAfterRegister(res: LoginResult<User>) {
    this.auth.forceLogin(res);
    const activePartner = await this._onOnline(res.user);
    this._redirectOnLogin(activePartner);
    return res;
  }

  public async login(login: string, password: string) {
    const res = await this.auth.login({ login, email: login, password, accessType: 'user' }, false).toPromise();
    await this._onOnline(res.user);
    const activePartner = await this._onOnline(res.user);
    this._redirectOnLogin(activePartner);
    if(this._socketIsConnected) this._loginSocket(); 
    return res;
  }

  private _socketConnected = () => {
    console.log('SOCKET CONNECTED');
    this._socketIsConnected = true;
    this.listeners.filter(l => l?.onSocketConnected).forEach(l => l?.onSocketConnected());
    if(this.auth.jwtToken) this._loginSocket();
  }

  private _loginSocket() {
    this.socket.emit('login', { JWT: this.auth.jwtToken }, () => {
      if (!this.listeners?.length) return;
      this.listeners.filter(l => l?.onSocketLoggedIn).forEach(l => l?.onSocketLoggedIn());
    });
  }

  private _socketDisconnected = () => {
    console.log('SOCKET DISCONNECTED');
    this._socketIsConnected = false;
    if (!this.listeners?.length) return;
    this.listeners.filter(l => l?.onSocketLoggedOut).forEach(l => l?.onSocketLoggedOut());
  }

  private async _onOffline() {
    const { activePartner } = await this.stayStore.init();
    return activePartner;
  }

  private async _onOnline(user: any) {
    try {
      await this.api.get('user/logged', {}, { customErrorHandler: true }).toPromise(); // Auth expiration protection: digoulasse
      const { activePartner } = await this.stayStore.init(getId(user));
      await this.loggedProfileStore.init(getId(user)).toPromise();
      await this.notificationStore.init().toPromise();
      return activePartner;
    } catch (error) {
      this.auth.logout(false);
      return await this._onOffline();
    }
  }

  private async _onLogout() {
    const { activePartner } = await this.stayStore.init();
    this.loggedProfileStore.reset();
    this.notificationStore.reset();
    return activePartner;
  }

  private async redirectToCachedUrlOrPath(path: string[], extras: NavigationExtras) {
    if(this._nextUrl?.length) {
      this.router.navigateByUrl(this._nextUrl, extras);
      this._nextUrl = null;
    } else {
      this.router.navigate(path, extras);
      this._nextUrl = null;
    }
  }

  private async _redirectOnLogout(activePartner: Partner) {
    if(!activePartner) this.router.navigate(this.cfg.stay.redirectOnPartnerInactiveLoggedOut, { replaceUrl: true }); 
    else this.router.navigate(['/home'], { replaceUrl: true });
  }

  private async _redirectOnLogin(activePartner: Partner) {
    if(!activePartner) this.router.navigate(this.cfg.stay.redirectOnPartnerInactiveLoggedIn, { replaceUrl: true }); 
    else this.redirectToCachedUrlOrPath(['/home'], { replaceUrl: true })
  }
}

export function initializeAuthFlow(authFlow: AuthFlowService) {
  return () => authFlow.initialize();
}